import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography,
    Box,
    Button,
    Autocomplete,
    TextField,
    CircularProgress,
    Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import { shortFunction, sip } from '../../translate';
import Api from '../../Api';
import { toast } from 'react-toastify';

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a';
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : '#6aa507';

const getItemId = (item) => item?.buddy || String(item?.id);

export default function SpyAdminCard({ item, allSips, adminChildrenMap = {}, onSaved }) {
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState([]);
    const [editing, setEditing] = useState(false);

    const adminBuddy = item?.buddy;

    const fetchOperators = useCallback(async () => {
        if (!adminBuddy) return;
        try {
            setLoading(true);
            const { data } = await Api.apyOperatorGet(adminBuddy);
            const list = data?.sip || [];
            const buddies = list.map((o) => o.spy || o.sip?.buddy).filter(Boolean);
            setOperators(buddies);
            setSelected(buddies);
        } catch (e) {
            console.error(e);
            setOperators([]);
            setSelected([]);
        } finally {
            setLoading(false);
        }
    }, [adminBuddy]);

    useEffect(() => {
        fetchOperators();
    }, [fetchOperators]);

    const handleSave = useCallback(async () => {
        if (!adminBuddy) return;
        try {
            setSaving(true);
            const parentAdmins = Object.entries(adminChildrenMap).filter(
                ([a, children]) => a !== adminBuddy && children.includes(adminBuddy)
            ).map(([a]) => a);
            const toSave = selected.filter((buddy) => !parentAdmins.includes(buddy));
            const list = toSave.map((buddy) => ({ value: buddy }));
            await Api.apyOperatorPost({ admin: adminBuddy, list });
            setOperators(toSave);
            setEditing(false);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            onSaved?.();
        } catch (e) {
            console.error(e);
            toast.error('Failed to save operators');
        } finally {
            setSaving(false);
        }
    }, [adminBuddy, selected, adminChildrenMap, langIndex, onSaved]);

    const parentAdmins = Object.entries(adminChildrenMap).filter(
        ([a, children]) => a !== adminBuddy && children.includes(adminBuddy)
    ).map(([a]) => a);
    const options = (allSips || []).filter(
        (s) => getItemId(s) !== adminBuddy && !parentAdmins.includes(getItemId(s))
    );
    const valueForAutocomplete = selected
        .map((buddy) => options.find((o) => getItemId(o) === buddy))
        .filter(Boolean);

    const handleChange = (_, newValue) => {
        setSelected(newValue.map((o) => getItemId(o)));
    };

    if (!item) return null;

    const label = `${item.notes ? `${item.notes} – ` : ''}${item.buddy}${item.issip ? ' (S)' : ''}`;
    const includedText = item?.included
        ? item.included.context
            ? `${item.included.context} – ${item.included.phone}`
            : item.included.phone
        : null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 280,
                maxWidth: 380,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'grey.50',
                borderLeft: '4px solid',
                borderLeftColor: color,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.2s, background-color 0.2s',
                '&:hover': {
                    bgcolor: 'grey.100',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                },
            }}
        >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: `${color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <PersonIcon sx={{ color, fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={label}>
                            {label}
                        </Typography>
                        {includedText && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" noWrap title={includedText}>
                                    {includedText}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {shortFunction.spy_operators_label?.[langIndex] || 'Operators who can spy'}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} sx={{ color }} />
                    </Box>
                ) : editing ? (
                    <>
                        <Autocomplete
                            multiple
                            size="small"
                            options={options}
                            value={valueForAutocomplete}
                            onChange={handleChange}
                            getOptionLabel={(opt) =>
                                `${opt.buddy || opt.id}${opt.notes ? ` – ${opt.notes}` : ''}`
                            }
                            isOptionEqualToValue={(opt, val) => getItemId(opt) === getItemId(val)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={sip.add_btn?.[langIndex] || 'Add'}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'background.paper',
                                            '& fieldset': { borderColor: 'divider' },
                                            '&:hover fieldset': { borderColor: color },
                                            '&.Mui-focused fieldset': { borderColor: color, borderWidth: 1 },
                                        },
                                    }}
                                />
                            )}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={getItemId(option)}
                                        label={`${option.buddy || option.id}${option.notes ? ` – ${option.notes}` : ''}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: `${color}60`,
                                            color: color,
                                            '& .MuiChip-deleteIcon': { color },
                                        }}
                                    />
                                ))
                            }
                            sx={{ mb: 1.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={saving}
                                onClick={() => { setSelected(operators); setEditing(false); }}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    '&:hover': { borderColor: 'text.secondary', backgroundColor: 'action.hover' },
                                }}
                            >
                                {shortFunction.spy_cancel?.[langIndex] || 'Cancel'}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={saving}
                                onClick={handleSave}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: color,
                                    color: color,
                                    '&:hover': {
                                        borderColor: hoverColor,
                                        color: hoverColor,
                                        backgroundColor: `${color}08`,
                                    },
                                }}
                            >
                                {saving
                                    ? (shortFunction.operator_loading?.[langIndex] || 'Saving...')
                                    : (shortFunction.spy_card_save?.[langIndex] || 'Save operators')}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            {(shortFunction.spy_operators_count?.[langIndex] || '{n} operators').replace('{n}', String(operators.length))}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setEditing(true)}
                            sx={{
                                textTransform: 'none',
                                borderColor: color,
                                color: color,
                                '&:hover': {
                                    borderColor: hoverColor,
                                    color: hoverColor,
                                    backgroundColor: `${color}08`,
                                },
                            }}
                        >
                            {shortFunction.spy_edit?.[langIndex] || 'Edit'}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
